/**
 * Context de Depósitos
 * Maneja el estado y operaciones relacionadas con depósitos
 */
import { createContext, useContext, useReducer, useCallback } from 'react';
import * as depositoService from '../services/depositoService';
import { useNotification } from './NotificationContext';

// Estado inicial
const initialState = {
  depositos: [],
  currentDeposit: null,
  filters: {},
  loading: false,
  error: null,
};

// Tipos de acciones
const DEPOSITO_ACTIONS = {
  FETCH_START: 'FETCH_START',
  FETCH_SUCCESS: 'FETCH_SUCCESS',
  FETCH_ERROR: 'FETCH_ERROR',
  CREATE_SUCCESS: 'CREATE_SUCCESS',
  UPDATE_SUCCESS: 'UPDATE_SUCCESS',
  DELETE_SUCCESS: 'DELETE_SUCCESS',
  SET_CURRENT_DEPOSIT: 'SET_CURRENT_DEPOSIT',
  CLEAR_CURRENT_DEPOSIT: 'CLEAR_CURRENT_DEPOSIT',
  SET_FILTERS: 'SET_FILTERS',
  CLEAR_FILTERS: 'CLEAR_FILTERS',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
};

// Reducer
const depositoReducer = (state, action) => {
  switch (action.type) {
    case DEPOSITO_ACTIONS.FETCH_START:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case DEPOSITO_ACTIONS.FETCH_SUCCESS:
      return {
        ...state,
        depositos: action.payload,
        loading: false,
        error: null,
      };

    case DEPOSITO_ACTIONS.FETCH_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    case DEPOSITO_ACTIONS.CREATE_SUCCESS:
      return {
        ...state,
        depositos: [...state.depositos, action.payload],
        currentDeposit: action.payload,
        loading: false,
        error: null,
      };

    case DEPOSITO_ACTIONS.UPDATE_SUCCESS:
      return {
        ...state,
        depositos: state.depositos.map((deposito) =>
          deposito.id === action.payload.id ? action.payload : deposito
        ),
        currentDeposit:
          state.currentDeposit?.id === action.payload.id
            ? action.payload
            : state.currentDeposit,
        loading: false,
        error: null,
      };

    case DEPOSITO_ACTIONS.DELETE_SUCCESS:
      return {
        ...state,
        depositos: state.depositos.filter(
          (deposito) => deposito.id !== action.payload
        ),
        currentDeposit:
          state.currentDeposit?.id === action.payload
            ? null
            : state.currentDeposit,
        loading: false,
        error: null,
      };

    case DEPOSITO_ACTIONS.SET_CURRENT_DEPOSIT:
      return {
        ...state,
        currentDeposit: action.payload,
      };

    case DEPOSITO_ACTIONS.CLEAR_CURRENT_DEPOSIT:
      return {
        ...state,
        currentDeposit: null,
      };

    case DEPOSITO_ACTIONS.SET_FILTERS:
      return {
        ...state,
        filters: { ...state.filters, ...action.payload },
      };

    case DEPOSITO_ACTIONS.CLEAR_FILTERS:
      return {
        ...state,
        filters: {},
      };

    case DEPOSITO_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
      };

    case DEPOSITO_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    default:
      return state;
  }
};

// Crear contexto
const DepositoContext = createContext(undefined);

// Provider
export const DepositoProvider = ({ children }) => {
  const [state, dispatch] = useReducer(depositoReducer, initialState);
  const { showSuccess, showError } = useNotification();

  /**
   * Obtener depósitos con filtros
   */
  const fetchDepositos = useCallback(
    async (filters = {}) => {
      try {
        dispatch({ type: DEPOSITO_ACTIONS.FETCH_START });

        const response = await depositoService.getAllDeposits(filters);

        // Extraer el array de depósitos de la respuesta anidada
        const depositos = response.data?.depositos || response.depositos || response.data || [];

        dispatch({
          type: DEPOSITO_ACTIONS.FETCH_SUCCESS,
          payload: depositos,
        });

        return depositos;
      } catch (error) {
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          'Error al obtener depósitos';

        dispatch({
          type: DEPOSITO_ACTIONS.FETCH_ERROR,
          payload: errorMessage,
        });

        showError(errorMessage);
        throw error;
      }
    },
    [showError]
  );

  /**
   * Crear nuevo depósito
   */
  const createDeposito = useCallback(
    async (data) => {
      try {
        dispatch({ type: DEPOSITO_ACTIONS.FETCH_START });

        const response = await depositoService.createDeposit(data);

        // Extraer el depósito de la respuesta
        const newDeposito = response.data || response;

        dispatch({
          type: DEPOSITO_ACTIONS.CREATE_SUCCESS,
          payload: newDeposito,
        });

        showSuccess('Depósito creado correctamente');
        return newDeposito;
      } catch (error) {
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          'Error al crear depósito';

        dispatch({
          type: DEPOSITO_ACTIONS.FETCH_ERROR,
          payload: errorMessage,
        });

        showError(errorMessage);
        throw error;
      }
    },
    [showSuccess, showError]
  );

  /**
   * Actualizar depósito
   */
  const updateDeposito = useCallback(
    async (id, data) => {
      try {
        dispatch({ type: DEPOSITO_ACTIONS.FETCH_START });

        const response = await depositoService.updateDeposit(id, data);
        const updatedDeposito = response.data || response;

        dispatch({
          type: DEPOSITO_ACTIONS.UPDATE_SUCCESS,
          payload: updatedDeposito,
        });

        showSuccess('Depósito actualizado correctamente');
        return updatedDeposito;
      } catch (error) {
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          'Error al actualizar depósito';

        dispatch({
          type: DEPOSITO_ACTIONS.FETCH_ERROR,
          payload: errorMessage,
        });

        showError(errorMessage);
        throw error;
      }
    },
    [showSuccess, showError]
  );

  /**
   * Eliminar depósito
   */
  const deleteDeposito = useCallback(
    async (id) => {
      try {
        dispatch({ type: DEPOSITO_ACTIONS.FETCH_START });

        await depositoService.deleteDeposit(id);

        dispatch({
          type: DEPOSITO_ACTIONS.DELETE_SUCCESS,
          payload: id,
        });

        showSuccess('Depósito eliminado correctamente');
      } catch (error) {
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          'Error al eliminar depósito';

        dispatch({
          type: DEPOSITO_ACTIONS.FETCH_ERROR,
          payload: errorMessage,
        });

        showError(errorMessage);
        throw error;
      }
    },
    [showSuccess, showError]
  );

  /**
   * Obtener depósito por ID
   */
  const getDepositoById = useCallback(
    async (id) => {
      try {
        dispatch({ type: DEPOSITO_ACTIONS.FETCH_START });

        const response = await depositoService.getDepositById(id);

        // Extraer el depósito de la respuesta
        const deposito = response.data || response;

        dispatch({
          type: DEPOSITO_ACTIONS.SET_CURRENT_DEPOSIT,
          payload: deposito,
        });

        dispatch({
          type: DEPOSITO_ACTIONS.FETCH_SUCCESS,
          payload: state.depositos,
        });

        return deposito;
      } catch (error) {
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          'Error al obtener depósito';

        dispatch({
          type: DEPOSITO_ACTIONS.FETCH_ERROR,
          payload: errorMessage,
        });

        showError(errorMessage);
        throw error;
      }
    },
    [showError, state.depositos]
  );

  /**
   * Descargar PDF del depósito
   */
  const downloadDepositPDF = useCallback(
    async (id, filename) => {
      try {
        await depositoService.downloadDepositPDF(id, filename);
        showSuccess('PDF descargado correctamente');
      } catch (error) {
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          'Error al descargar PDF';

        showError(errorMessage);
        throw error;
      }
    },
    [showSuccess, showError]
  );

  /**
   * Confirmar depósito
   */
  const confirmDeposit = useCallback(
    async (id) => {
      try {
        dispatch({ type: DEPOSITO_ACTIONS.FETCH_START });

        const response = await depositoService.confirmDeposit(id);
        const confirmed = response.data || response;

        dispatch({
          type: DEPOSITO_ACTIONS.UPDATE_SUCCESS,
          payload: confirmed,
        });

        showSuccess('Depósito confirmado correctamente');
        return confirmed;
      } catch (error) {
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          'Error al confirmar depósito';

        dispatch({
          type: DEPOSITO_ACTIONS.FETCH_ERROR,
          payload: errorMessage,
        });

        showError(errorMessage);
        throw error;
      }
    },
    [showSuccess, showError]
  );

  /**
   * Cancelar depósito
   */
  const cancelDeposit = useCallback(
    async (id, motivo) => {
      try {
        dispatch({ type: DEPOSITO_ACTIONS.FETCH_START });

        const response = await depositoService.cancelDeposit(id, motivo);
        const cancelled = response.data || response;

        dispatch({
          type: DEPOSITO_ACTIONS.UPDATE_SUCCESS,
          payload: cancelled,
        });

        showSuccess('Depósito cancelado correctamente');
        return cancelled;
      } catch (error) {
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          'Error al cancelar depósito';

        dispatch({
          type: DEPOSITO_ACTIONS.FETCH_ERROR,
          payload: errorMessage,
        });

        showError(errorMessage);
        throw error;
      }
    },
    [showSuccess, showError]
  );

  /**
   * Limpiar depósito actual
   */
  const clearCurrentDeposit = useCallback(() => {
    dispatch({ type: DEPOSITO_ACTIONS.CLEAR_CURRENT_DEPOSIT });
  }, []);

  /**
   * Establecer filtros
   */
  const setFilters = useCallback((filters) => {
    dispatch({
      type: DEPOSITO_ACTIONS.SET_FILTERS,
      payload: filters,
    });
  }, []);

  /**
   * Limpiar filtros
   */
  const clearFilters = useCallback(() => {
    dispatch({ type: DEPOSITO_ACTIONS.CLEAR_FILTERS });
  }, []);

  /**
   * Limpiar error
   */
  const clearError = useCallback(() => {
    dispatch({ type: DEPOSITO_ACTIONS.CLEAR_ERROR });
  }, []);

  const value = {
    // Estado
    depositos: state.depositos,
    currentDeposit: state.currentDeposit,
    filters: state.filters,
    loading: state.loading,
    error: state.error,

    // Funciones
    fetchDepositos,
    createDeposito,
    updateDeposito,
    deleteDeposito,
    getDepositoById,
    downloadDepositPDF,
    confirmDeposit,
    cancelDeposit,
    clearCurrentDeposit,
    setFilters,
    clearFilters,
    clearError,
  };

  return (
    <DepositoContext.Provider value={value}>
      {children}
    </DepositoContext.Provider>
  );
};

// Hook personalizado
export const useDeposito = () => {
  const context = useContext(DepositoContext);
  if (context === undefined) {
    throw new Error('useDeposito debe ser usado dentro de un DepositoProvider');
  }
  return context;
};

export default DepositoContext;
